import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  UncontrolledTooltip,
  Input,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import LangModal from "components/Common/LangModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { get, post, patch, del } from "../../../helpers/api_helper";

const API_BASE = "https://buharaapi.stechomeyazilim.info";

const ContactsList = () => {
  //meta title
  document.title = "Blog Kategorileri | BUHARA ADMIN PANELI";

  const tableRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [contact, setContact] = useState();
  const [contactEdit, setEditContact] = useState();
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [langModal, setLangModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // ── Kategorileri çek ──────────────────────────────────────────
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await get(`${API_BASE}/Blog_Category`);
      setUsers(res?.value || res || []);
    } catch (err) {
      console.error("Blog_Category fetch error:", err);
      toastr.error("Kategoriler yüklenirken hata oluştu!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ── Form ──────────────────────────────────────────────────────
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      categoryName: (contactEdit && contactEdit.Blog_Category_Title) || "",
    },
    validationSchema: Yup.object({
      categoryName: Yup.string().required("Kategori adını giriniz"),
    }),
    onSubmit: async values => {
      try {
        if (isEdit && contactEdit?.ID) {
          await patch(`${API_BASE}/Blog_Category(${contactEdit.ID})`, {
            Blog_Category_Title: values.categoryName,
          });
          toastr.success("Kategori güncellendi.");
        } else {
          await post(`${API_BASE}/Blog_Category`, {
            Blog_Category_Title: values.categoryName,
          });
          toastr.success("Kategori eklendi.");
        }
        setIsEdit(false);
        validation.resetForm();
        toggle();
        fetchCategories();
      } catch (err) {
        console.error("Blog_Category save error:", err);
        toastr.error("Kaydetme sırasında bir hata oluştu!");
      }
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: "#",
        Cell: () => {
          return <input type="checkbox" className="form-check-input" />;
        },
      },
      {
        Header: "ID",
        accessor: "ID",
        filterable: true,
        Cell: cellProps => (cellProps.value != null ? cellProps.value : ""),
      },
      {
        Header: "Kategori Adı",
        accessor: "Blog_Category_Title",
        filterable: true,
        Cell: cellProps => (cellProps.value ? cellProps.value : ""),
      },
      {
        Header: "İşlemler",
        Cell: cellProps => {
          return (
            <div className="d-flex gap-3">
              <Link
                to="#"
                className="text-success"
                onClick={() => {
                  const userData = cellProps.row.original;
                  handleUserClick(userData);
                }}
              >
                <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
                <UncontrolledTooltip placement="top" target="edittooltip">
                  Düzenle
                </UncontrolledTooltip>
              </Link>
              <Link
                to="#"
                className="text-info"
                onClick={() => {
                  setSelectedItem(cellProps.row.original);
                  setLangModal(true);
                }}
              >
                <i className="mdi mdi-translate font-size-18" id="langtooltip-bcat" />
                <UncontrolledTooltip placement="top" target="langtooltip-bcat">
                  Çeviriler
                </UncontrolledTooltip>
              </Link>
              <Link
                to="#"
                className="text-danger"
                onClick={() => {
                  const userData = cellProps.row.original;
                  onClickDelete(userData);
                }}
              >
                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
                <UncontrolledTooltip placement="top" target="deletetooltip">
                  Sil
                </UncontrolledTooltip>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  const toggle = () => {
    setModal(!modal);
  };

  const handleUserClick = arg => {
    setEditContact(arg);
    setIsEdit(true);
    toggle();
  };

  const handleUserClicks = () => {
    setEditContact(null);
    setIsEdit(false);
    toggle();
  };

  const onClickDelete = user => {
    setContact(user);
    setDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (contact && contact.ID) {
      try {
        await del(`${API_BASE}/Blog_Category(${contact.ID})`);
        toastr.success("Kategori silindi.");
        fetchCategories();
      } catch (err) {
        console.error("Blog_Category delete error:", err);
        toastr.error("Silme sırasında bir hata oluştu!");
      }
    }
    setDeleteModal(false);
  };

  return (
    <React.Fragment>
      <LangModal
        isOpen={langModal}
        toggle={() => setLangModal(false)}
        sourceRecord={selectedItem}
        title="Blog Kategorisi"
        tableName="Blog_Category_Lang"
        foreignKey="Category_ID"
        foreignKeyValue={selectedItem?.ID}
        fields={[
          { name: "Blog_Category_Title", label: "Kategori Adı", type: "text" },
        ]}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Blog Kategorileri" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading && <div className="loading-screen">Yükleniyor...</div>}

                  <TableContainer
                    columns={columns}
                    data={users}
                    ref={tableRef}
                    isGlobalFilter={true}
                    isAddBlogCategory={true}
                    handleOrderClicks={handleUserClicks}
                    customPageSize={10}
                    className="custom-header-css"
                  />

                  <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle} tag="h4">
                      {!!isEdit ? "Kategori Düzenle" : "Kategori Ekle"}
                    </ModalHeader>
                    <ModalBody>
                      <Form
                        onSubmit={e => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <Row>
                          <Col xs={12}>
                            <div className="mb-3">
                              <Label className="form-label">Kategori Adı</Label>
                              <Input
                                name="categoryName"
                                label="Kategori Adı"
                                type="text"
                                placeholder="Kategori Adını Giriniz"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.categoryName || ""}
                                invalid={
                                  validation.touched.categoryName &&
                                  validation.errors.categoryName
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.categoryName &&
                              validation.errors.categoryName ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.categoryName}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="text-end">
                              <button
                                type="submit"
                                className="btn btn-success save-user"
                              >
                                Kaydet
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </ModalBody>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ContactsList);
